import { useCallback } from 'react';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';
import openNativePicker from '@/requests/cloud-storage/openNativePicker';
import uploadToCDN from '@/requests/cloud-storage/uploadToCDN';
import { isLocalUrl, resolveLocalUrl } from '@/utils/canvas/localUrlResolver';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';
import { InputType } from '@/types';

import type { IImageInput, IVideoInput } from '@/types';

type UpdateInput = (args: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

interface UseNativePickerOptions {
  updateInput: UpdateInput;
  isAuthorized: boolean | undefined;
  addPendingUpload: (upload: { id: string; file: File }) => void;
}

const useNativePicker = ({
  updateInput,
  isAuthorized,
  addPendingUpload,
}: UseNativePickerOptions) => {
  const handleSelectFromNativePicker = useCallback(
    async (id: string, inputType: InputType) => {
      // Don't show login popup here - allow user to complete input
      // Login will appear when user clicks Generate

      if (isAuthorized) {
        sendAiGenScreenActionAnalytics({
          action: AiGenScreenAction.UPLOAD,
          screen_type: ScreenType.GENERATOR,
        });
      }

      // Set loading immediately for visual feedback
      updateInput({
        id,
        value: { isLoading: true } as Partial<IImageInput | IVideoInput>,
      });

      let localPreviewUrl: string | null = null;

      try {
        const allowedFormats: ('photo' | 'video')[] =
          inputType === InputType.VIDEO ? ['video'] : ['photo'];

        const result = await openNativePicker({
          allowedFormats,
          multiSelect: false,
          maxCount: 1,
        });

        if (!result?.files?.length) {
          // User cancelled picker - clear loading state
          updateInput({
            id,
            value: { isLoading: false } as Partial<IImageInput | IVideoInput>,
          });
          return;
        }

        const file = result.files[0];
        const url = file?.url;

        if (!url) {
          updateInput({
            id,
            value: { isLoading: false, hasUploadError: true } as Partial<
              IImageInput | IVideoInput
            >,
          });
          return;
        }

        if (isLocalUrl(url)) {
          // Local file - need to upload to CDN
          const resolvedUrl = resolveLocalUrl(url);
          const response = await fetch(resolvedUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch local file: ${response.status}`);
          }
          const blob = await response.blob();

          // Create local preview for instant feedback
          localPreviewUrl = URL.createObjectURL(blob);

          // If not authorized, show local preview and store file for later CDN upload
          // Login popup will appear when user clicks Generate
          if (!isAuthorized) {
            const uploadedFile = new File([blob], file.id, {
              type: blob.type,
            });
            updateInput({
              id,
              value: {
                src: localPreviewUrl,
                isLoading: false,
                hasUploadError: false,
              } as Partial<IImageInput | IVideoInput>,
            });
            addPendingUpload({ id, file: uploadedFile });
            return; // Keep blob URL, don't revoke
          }

          updateInput({
            id,
            value: { src: localPreviewUrl, isLoading: true } as Partial<
              IImageInput | IVideoInput
            >,
          });

          // Upload to CDN
          const uploadedFile = new File([blob], file.id, { type: blob.type });
          const cdnUrl = await uploadToCDN(uploadedFile);

          if (cdnUrl) {
            updateInput({
              id,
              value: {
                src: cdnUrl,
                isLoading: false,
                hasUploadError: false,
              } as Partial<IImageInput | IVideoInput>,
            });
          } else {
            updateInput({
              id,
              value: {
                src: null,
                isLoading: false,
                hasUploadError: true,
              } as Partial<IImageInput | IVideoInput>,
            });
          }
        } else {
          // Already a CDN URL - use directly
          updateInput({
            id,
            value: {
              src: url,
              isLoading: false,
              hasUploadError: false,
            } as Partial<IImageInput | IVideoInput>,
          });
        }
      } catch (error) {
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
        updateInput({
          id,
          value: {
            src: null,
            isLoading: false,
            hasUploadError: true,
          } as Partial<IImageInput | IVideoInput>,
        });
      } finally {
        // Clean up local preview blob URL if it was created AND we're done with it
        // Don't revoke if we stored it for unauthorized users (they need the preview)
        if (localPreviewUrl && isAuthorized) {
          URL.revokeObjectURL(localPreviewUrl);
        }
      }
    },
    [updateInput, isAuthorized, addPendingUpload]
  );

  return { handleSelectFromNativePicker };
};

export default useNativePicker;
